import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { debug } from 'console';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { PAGE_NAMES, ROLES } from '../../../core/helpers/constants';
import { IPermission, IUser } from '../../../core/models/auth.models';
import { AppService } from '../../../store/app.service';
import { UsersApiService } from '../users.apiservice';
import { UsersService } from '../users.service';
import { InstanceService } from '../../../core/instance/instance.service';
import { ISearch } from '../../../core/models/search.model';

@Component({
  selector: 'app-users-main',
  templateUrl: './users-main.component.html',
  styleUrls: ['./users-main.component.scss'],
})
export class UsersMainComponent implements OnInit, OnDestroy {
  @Input() usersData: Array<IUser> = []; // Add Input property for usersData
  doWhat: IPermission = {} as IPermission;
  tablePermissions: IPermission = {} as IPermission;
  columns: Array<any> = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  activeUser: IUser = {} as IUser;
  savedSearchId: any;
  userSavedSearches: ISearch[] = [];
  selectedSavedSearchId: string | null = null;

  constructor(
    private userApiService: UsersApiService,
    private route: ActivatedRoute,
    private appService: AppService,
    private userService: UsersService,
    private instance: InstanceService,
    private translate: TranslateService
  ) {
    this.savedSearchId = null;
  }

  private syncTablePermissions(): void {
    const isShopManager = this.activeUser.roles?.some(r => r.code === ROLES.ShopManager.Code) ?? false;
    this.tablePermissions = {
      ...this.doWhat,
      canDelete: this.doWhat.canDelete && !isShopManager,
    };
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('USERS');

    this.columns = [
      ...(!this.instance.isLastNameFirst ? [{ title: 'FIRST_NAME', field: 'firstName', sortable: true, searchable: true }] : [{ title: 'LAST_NAME', field: 'lastName', sortable: true, searchable: true }]),
      ...(!this.instance.isLastNameFirst ? [{ title: 'LAST_NAME', field: 'lastName', sortable: true, searchable: true }] :[{ title: 'FIRST_NAME', field: 'firstName', sortable: true, searchable: true }]),
      { title: 'EMAIL', field: 'email', searchable: true },
      { title: 'ROLE', field: 'code', pipe: 'P_RolesName_Filter', searchable: true },
      { title: 'LAST_LOGIN', field: 'lastLoginDate', sortable: true, searchable: true, pipe: 'P_AppDateFormat', pipeArgs: ['MM/DD/YYYY HH:mm'] },
    ];

    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Users);
    this.syncTablePermissions();

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: IUser) => {
        this.activeUser = response;
        this.syncTablePermissions();
      });
    if (this.usersData.length === 0) { // Fetch users data only if not provided by parent
      this.appService.getAllUsers$()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: Array<IUser>) => {
          this.usersData = [...response];

          // console.log([...response]);
        });
      this.savedSearchId = this.route.snapshot.queryParams['savedsearchid'];
      this.loadSavedSearches();
      this.appService.fetchAllUsers(this.savedSearchId);
    }
  }

  private loadSavedSearches() {
    this.appService.fetchSearches();
    this.appService.getAllSearches$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searches: ISearch[]) => {
        this.userSavedSearches = (searches || [])
          .filter(search => search.resultType?.toLowerCase() === 'user');
        this.selectedSavedSearchId = this.userSavedSearches.some(search => search.id === this.savedSearchId)
          ? this.savedSearchId
          : null;
      });
  }

  onSavedSearchChange(savedSearchId: string | null) {
    this.selectedSavedSearchId = savedSearchId || null;
    this.savedSearchId = savedSearchId || '';
    this.appService.fetchAllUsers(this.savedSearchId);
  }

  editRow(evt: any) {
    this.userService.openEditUserModal(evt)
  }

  onAddNew() {
    if (!this.doWhat.isAdmin) {
      if (this.activeUser.roles.some(r => [ROLES.MarketingManager.Code].includes(r.code))) {
        if (this.activeUser.marketingManagerOf.length === 0) {
          Swal.fire(this.translate.instant('USERS'), this.translate.instant('USERS_NEED_ASSIGNED_COMPANY_FIRST'), 'warning');
          return;
        }
      }
      else if (this.activeUser.roles.some(r => [ROLES.CompanyOwner.Code].includes(r.code))) {
        if (this.activeUser.companyOwnerOf.length === 0) {
          Swal.fire(this.translate.instant('USERS'), this.translate.instant('USERS_NEED_ASSIGNED_COMPANY_FIRST'), 'warning');
          return;
        }
      }
      else if (this.activeUser.roles.some(r => [ROLES.ClientOwner.Code].includes(r.code))) {
        if (this.activeUser.clientOwnerOf.length === 0) {
          Swal.fire(this.translate.instant('USERS'), this.translate.instant('USERS_NEED_ASSIGNED_CLIENT_FIRST'), 'warning');
          return;
        }
      }
    }

    this.userService.openEditUserModal({ id: null })
  }

  deleteRows(evt: any) {
    this.userApiService.deleteUser(evt)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.appService.fetchAllUsers(this.savedSearchId);
      })

  }
  archiveRows(evt: any) {
    this.userApiService.archiveUser(evt)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.appService.fetchAllUsers(this.savedSearchId);
      })

  }
  unarchiveRows(evt: any) {
    this.userApiService.unarchiveUser(evt)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.appService.fetchAllUsers(this.savedSearchId);
      })

  }
  onClientRowClick(evt: any) {
    if(evt.canBeEdit == null || evt.canBeEdit){
      this.userService.openEditUserModal(evt);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
